import { BaseDataTablePageDirective } from "@app/view/shared/base-pages/base-data-table-page/base-data-table-page.directive";
import { Component, Input, OnInit, inject } from "@angular/core";
import { PlanningSegment } from "@app/models/rest/planning-segment";
import { SystemSearchMetaRestService } from "@app/services/hal/system-search-meta-rest.service";
import { PlanningSegmentStore } from "@app/services/store/planning-segment-store";
import { Store } from "@ngrx/store";
import { MatDialog } from "@angular/material/dialog";
import { UntypedFormBuilder } from "@angular/forms";
import { Router } from "@angular/router";
import { ServiceErrorHandlerService } from "@app/view/shared/service-error-handler.service";
import { NgxUiLoaderService } from "ngx-ui-loader";
import { PriorityChooserComponent } from "@app/view/shared/priority-chooser/priority-chooser.component";
import { PlanningSegmentRuleMode } from "@app/models/enums/planning-segment-rule.enum";
import { Model } from "@bryntum/core-thin";
import { Column } from "@bryntum/grid-thin";
import { ConfirmDialogComponent } from "@app/view/shared/confirm-dialog/confirm-dialog.component";
import { MatToolbar } from "@angular/material/toolbar";
import { MatIconButton, MatButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { GridComponent } from "../../../shared/base-pages/base-data-table-page/grid.component";
import { MatPaginator } from "@angular/material/paginator";
import { TranslatePipe } from "@ngx-translate/core";

@Component({
  selector: "app-planning-segment-list",
  templateUrl: "./planning-segment-list.component.html",
  styleUrls: ["./planning-segment-list.component.scss"],
  imports: [
    MatToolbar,
    MatIconButton,
    MatIcon,
    MatButton,
    GridComponent,
    MatPaginator,
    TranslatePipe
  ]
})
export class PlanningSegmentListComponent
  extends BaseDataTablePageDirective<PlanningSegment>
  implements OnInit
{
  planningSegmentStore: PlanningSegmentStore;
  protected store: Store<any>;
  protected dialog: MatDialog;
  protected formBuilder: UntypedFormBuilder;
  protected router = inject(Router);
  serverErrorHandler: ServiceErrorHandlerService;
  protected ngxUiLoaderService: NgxUiLoaderService;

  @Input()
  public columns: object[] = [
    {
      field: "code",
      text: "Code",
      flex: 1
    },
    {
      field: "description",
      text: "Description",
      flex: 1,
      editor: true
    },
    {
      field: "startHour",
      text: "Hour",
      type: "number",
      min: 0,
      max: 23,
      width: 40
    },
    {
      field: "startMinute",
      text: "Minute",
      type: "number",
      min: 0,
      max: 59,
      width: 40
    },
    {
      field: "duration",
      text: "Duration (min)",
      type: "number",
      min: 0,
      max: 2440
    },
    {
      text: "Days",
      collapsible: true,
      headerRenderer: ({ headerElement }) => {
        headerElement.style.backgroundColor = "#152643";
        return "Valid Day Rules";
      },
      children: [
        { field: "monday", text: "Mon", type: "check", width: 40 },
        { field: "tuesday", text: "Tue", type: "check", width: 40 },
        { field: "wednesday", text: "Wed", type: "check", width: 40 },
        { field: "thursday", text: "Thu", type: "check", width: 40 },
        { field: "friday", text: "Fri", type: "check", width: 40 },
        { field: "saturday", text: "Sat", type: "check", width: 40 },
        { field: "sunday", text: "Sun", type: "check", width: 40 }
      ]
    },
    {
      text: "Priority Type Rules",
      collapsible: true,
      headerRenderer: ({ headerElement }) => {
        headerElement.style.backgroundColor = "#1b3055";
        return "Priority Type Rules";
      },
      children: [
        {
          field: "showForNormal",
          text: "Normal",
          type: "check",
          width: 80
        },
        {
          field: "showForHigh",
          text: "High",
          type: "check",
          width: 80
        },
        {
          field: "showForEmergency",
          text: "Emergency",
          type: "check",
          width: 80
        }
      ]
    },
    {
      field: "codeMode",
      text: "Priority Mode",
      editor: {
        type: "dropdown",
        items: [
          { text: "None", value: PlanningSegmentRuleMode.NONE },
          { text: "Include", value: PlanningSegmentRuleMode.INCLUDE },
          { text: "Exclude", value: PlanningSegmentRuleMode.EXCLUDE }
        ]
      },
      renderer: ({ value }) => {
        switch (value) {
          case PlanningSegmentRuleMode.INCLUDE:
            return "Include";
          case PlanningSegmentRuleMode.EXCLUDE:
            return "Exclude";
          case PlanningSegmentRuleMode.NONE:
          default:
            return "None";
        }
      }
    },
    {
      id: "priority-column",
      text: "Priorities",
      type: "template",
      field: "filterPriorities",
      flex: 1,
      cellMenuItems: [
        {
          text: "Edit Priority Codes",
          icon: "b-fa b-fa-edit",
          onItem: this.priorityCodeChooser.bind(this)
        }
      ],
      template: ({ record }) => {
        const priorities = record.filterPriorities;
        if (!priorities || priorities.length === 0) {
          return "None";
        }
        return priorities.map((p: any) => p.code).join(", ");
      }
    }
  ];

  constructor() {
    const systemSearchMetaRestService = inject(SystemSearchMetaRestService);
    const planningSegmentStore = inject(PlanningSegmentStore);
    const store = inject<Store<any>>(Store);
    const dialog = inject(MatDialog);
    const formBuilder = inject(UntypedFormBuilder);
    const serverErrorHandler = inject(ServiceErrorHandlerService);
    const ngxUiLoaderService = inject(NgxUiLoaderService);

    super(
      systemSearchMetaRestService,
      planningSegmentStore,
      store,
      dialog,
      "PlanningSegment",
      formBuilder,
      serverErrorHandler,
      ngxUiLoaderService
    );
    this.planningSegmentStore = planningSegmentStore;
    this.store = store;
    this.dialog = dialog;
    this.formBuilder = formBuilder;
    this.serverErrorHandler = serverErrorHandler;
    this.ngxUiLoaderService = ngxUiLoaderService;

    this.priorityCodeChooser = this.priorityCodeChooser.bind(this);
    this.onCellClick = this.onCellClick.bind(this);
  }

  public get pristine() {
    return (
      !this.gridStore.changes ||
      (this.gridStore.changes.added.length === 0 &&
        this.gridStore.changes.removed.length === 0 &&
        this.gridStore.changes.modified.length === 0)
    );
  }

  ngOnInit() {
    super.ngOnInit();
    this.gridStore.autoCommit = false;
    this.gridStore.load({});
  }

  addRow() {
    this.gridStore.insert(0, {
      code: "",
      description: "",
      startHour: 9,
      startMinute: 0,
      duration: 240,
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: false,
      sunday: false,
      showForNormal: true,
      showForHigh: true,
      showForEmergency: true,
      cpdeMode: "None"
    });
  }
  onCellClick(event: { record: Model; column: Column }) {
    if (event.column.id === "priority-column") {
      this.priorityCodeChooser({ record: event.record });
    }
  }
  priorityCodeChooser(data: { record: any }) {
    const dialogRef = this.dialog.open(PriorityChooserComponent, {
      data: {
        segment: data.record.originalData
      }
    });

    dialogRef.afterClosed().subscribe((value) => {
      // After changes are saved, reload the store to refresh the grid data
      this.gridStore.load({}).then();
    });
  }

  showHelp() {
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: "Segment Visibility Help",
        message:
          "Slot visibility is determined by both priority type rules and slot rules. First, the priority type rules set which priorities can generally see the slot. Then, the slot rule you choose is applied. Selecting 'Include' means that only the specified priorities will see this slot. Selecting 'Exclude' means that every priority will see it except those you've specified.",
        showCancelButton: false
      }
    });
  }
}
